import axios from "axios";
import { useState, useEffect, useRef, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  Settings,
  FileText,
  Users,
  LogOut,
  FolderOpen,
  X,
  Check,
  Wand2,
  Bot,
  ExternalLink,
  Shield,
  AlertTriangle,
} from "lucide-react";
import {
  createPortalApi,
  portalLogin,
  fetchDocuments,
  fetchSubclients,
  fetchChatbotUsers,
  fetchPortalQuotas,
  uploadFiles,
  initChunkedUpload,
  uploadChunk,
  completeChunkedUpload,
  requestDocumentSync,
  cancelDocumentSync,
  deleteDocument,
  bulkDeleteDocuments,
  assignDocument,
  createChatbotUser,
  deleteChatbotUser,
  updateChatbotUserPassword,
  updateChatbotUser,
  rebalanceMessagePercentsEqually,
  clearPortalAuthStorage,
  getStoredPortalClient,
  getStoredPortalToken,
  savePortalSession,
  saveStoredPortalClient,
} from "../../api/portal";
import { generatePassword } from "../../utils/password";
import {
  formatApiErrorDetail,
  formatSize,
  formatUploadError,
  messagesFromQuotaPercent,
} from "../../utils/format";
import { getAgentTokenExpiryStatus } from "../../utils/portalAgentToken";

import { usePortalSlugFromRoute } from "../../hooks/usePortalSlugFromRoute";
import Modal from "../../components/Modal";
import LoadingOverlay from "../../components/LoadingOverlay";
import PortalLoginView from "./PortalLoginView";
import DocumentsTab from "./DocumentsTab";
import SubClientsTab from "./SubClientsTab";
import UsersTab from "./UsersTab";
import SettingsTab from "./SettingsTab";
import { brandSoftStyle } from "../../utils/branding";

/**
 * État initial du modal d’accès groupé : reflète ce que TOUS les documents sélectionnés ont en commun.
 * - Accès général : coché seulement si chaque document l’a.
 * - Sous-client : coché seulement si chaque document est lié à ce sous-client (par nom).
 */
function deriveBulkAccessInitialState(documents, selectedDocIds, subclients) {
  const docs = documents.filter((d) => selectedDocIds.includes(d.id));
  if (!docs.length) {
    return { generalAccess: false, selectedSid: [] };
  }
  const generalAccess = docs.every((d) => d.subclient_general);
  const selectedSid = subclients
    .reduce((acc, sc) => {
      if (docs.every((d) => (d.subclients || []).includes(sc.name))) {
        acc.push(sc.id);
      }
      return acc;
    }, []);
  return { generalAccess, selectedSid };
}

/**
 * Portail : JWT stocké en localStorage (lisible par le JS). En cas de XSS sur cette origine,
 * un script pourrait exfiltrer le token — mitigations : CSP, pas de HTML arbitraire, deps à jour.
 * Évolution possible : cookies HttpOnly (comme le chatbot). Voir docs/SECURITY_DEPLOYMENT.md.
 */
function getClientSlugFromPortalToken(token) {
  if (!token || typeof token !== "string") return null;
  try {
    const part = token.split(".")[1];
    if (!part) return null;
    const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const payload = JSON.parse(atob(base64));
    if (payload.type !== "portal") return null;
    return typeof payload.sub === "string" ? payload.sub : null;
  } catch {
    return null;
  }
}

function getPortalPolicyStorageKey(clientSlug) {
  return `portal_platform_privacy_accepted_${clientSlug || "unknown"}`;
}

async function walkDataTransferEntry(entry, path = "") {
  if (entry.isFile) {
    return new Promise((resolve) => {
      entry.file(
        (file) => {
          const fullPath = path ? `${path}/${file.name}` : file.name;
          Object.defineProperty(file, "webkitRelativePath", {
            value: fullPath,
            configurable: true,
          });
          resolve([file]);
        },
        () => resolve([]),
      );
    });
  }
  if (entry.isDirectory) {
    const reader = entry.createReader();
    const allEntries = [];
    let batch = await new Promise((resolve) =>
      reader.readEntries(
        (entries) => resolve(entries || []),
        () => resolve([]),
      ),
    );
    while (batch.length > 0) {
      allEntries.push(...batch);
      batch = await new Promise((resolve) =>
        reader.readEntries(
          (entries) => resolve(entries || []),
          () => resolve([]),
        ),
      );
    }
    const files = [];
    for (const subEntry of allEntries) {
      const subPath = path ? `${path}/${entry.name}` : entry.name;
      const subFiles = await walkDataTransferEntry(subEntry, subPath);
      files.push(...subFiles);
    }
    return files;
  }
  return [];
}

async function collectDroppedItems(items) {
  const files = [];
  const entries = [];
  for (let i = 0; i < items.length; i++) {
    const item = items[i];
    if (item.kind !== "file") continue;
    const entry =
      typeof item.webkitGetAsEntry === "function"
        ? item.webkitGetAsEntry()
        : null;
    if (entry) {
      entries.push(entry);
    } else {
      const file = item.getAsFile();
      if (file) files.push(file);
    }
  }
  for (const entry of entries) {
    const subFiles = await walkDataTransferEntry(entry);
    files.push(...subFiles);
  }
  return files;
}

export default function ClientPortal() {
  const { slug: slugFromRoute } = useParams();

  const [token, setToken] = useState(getStoredPortalToken);
  const [clientInfo, setClientInfo] = useState(null);
  const [tab, setTab] = useState("documents");

  const [slug, setSlug] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");
  const [hasSso, setHasSso] = useState(false);

  const [documents, setDocuments] = useState([]);
  const [subclients, setSubclients] = useState([]);
  const [chatbotUsers, setChatbotUsers] = useState([]);
  const [portalQuotas, setPortalQuotas] = useState({});
  const [equalSplitLoading, setEqualSplitLoading] = useState(false);
  const [selectedSubclient, setSelectedSubclient] = useState(null);

  const [maxStorage, setMaxStorage] = useState(524288000);
  const [currentStorage, setCurrentStorage] = useState(0);
  const [portalSync, setPortalSync] = useState({});
  const [syncLoading, setSyncLoading] = useState(false);
  const [syncCancelLoading, setSyncCancelLoading] = useState(false);
  const [agentTokenAlertTick, setAgentTokenAlertTick] = useState(0);

  const [showUserModal, setShowUserModal] = useState(false);
  const [newUser, setNewUser] = useState({
    username: "",
    password: "",
    subclient_id: null,
  });
  const [editingUserId, setEditingUserId] = useState(null);
  const [editUserPassword, setEditUserPassword] = useState("");
  const [editUserSubclient, setEditUserSubclient] = useState(null);
  const [editUserPercent, setEditUserPercent] = useState("");

  const [isUploading, setIsUploading] = useState(false);
  const [assignModal, setAssignModal] = useState(null);
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [bulkAccessModal, setBulkAccessModal] = useState(null);
  const [hasAcceptedPlatformPolicy, setHasAcceptedPlatformPolicy] =
    useState(() => {
      const key = getPortalPolicyStorageKey(
        (() => {
          try {
            const raw = getStoredPortalClient();
            return raw.slug || "";
          } catch { return ""; }
        })(),
      );
      return localStorage.getItem(key) === "1";
    });

  const fileInputRef = useRef(null);

  usePortalSlugFromRoute(slugFromRoute, setSlug);

  const api = useMemo(
    () =>
      createPortalApi(
        token,
        (newToken) => setToken(newToken),
        () => {
          setToken("");
          setClientInfo(null);
        },
      ),
    [token],
  );

  /** Slug fiable : localStorage, JWT portail, URL ou champ de connexion */
  const clientSlug = useMemo(() => {
    const fromStorage = clientInfo?.slug || clientInfo?.client_slug;
    if (fromStorage) return fromStorage;
    const fromJwt = getClientSlugFromPortalToken(token);
    if (fromJwt) return fromJwt;
    if (slugFromRoute) {
      try {
        return decodeURIComponent(slugFromRoute).trim().toLowerCase();
      } catch {
        return slugFromRoute.trim().toLowerCase();
      }
    }
    return slug.trim().toLowerCase() || null;
  }, [clientInfo, token, slugFromRoute, slug]);

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!clientSlug || clientSlug.length < 2) {
      setHasSso(false);
      return;
    }
    const timer = setTimeout(() => {
      fetch(`/api/c/${encodeURIComponent(clientSlug)}/config`)
        .then((res) => (res.ok ? res.json() : null))
        .then((data) => {
          if (data) setHasSso(data.has_sso || false);
          else setHasSso(false);
        })
        .catch(() => setHasSso(false));
    }, 500);
    return () => clearTimeout(timer);
  }, [clientSlug]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const agentTokenBanner = useMemo(() => {
    if (!clientSlug) return null;
    if (portalSync?.local_agent_enabled !== true) return null;
    const st = getAgentTokenExpiryStatus(clientSlug);
    if (st.kind === "expired") {
      return {
        type: "expired",
        text: "Le jeton de l’agent local (dossier RAGUIA) enregistré sur ce navigateur a expiré. Générez un nouveau jeton et mettez à jour la configuration sur les postes concernés.",
      };
    }
    if (st.kind === "warning") {
      const j = Math.max(0, Math.ceil(st.daysRemaining));
      return {
        type: "warning",
        text: `Le jeton de l’agent local expire dans environ ${j} jour(s). Pensez à le renouveler dans Paramètres et à mettre à jour vos machines.`,
      };
    }
    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientSlug, agentTokenAlertTick, portalSync?.local_agent_enabled]);

  useEffect(() => {
    const onUpdate = () => setAgentTokenAlertTick((t) => t + 1);
    window.addEventListener("portal-agent-token-updated", onUpdate);
    return () =>
      window.removeEventListener("portal-agent-token-updated", onUpdate);
  }, []);

  useEffect(() => {
    if (!clientSlug) return undefined;
    const id = window.setInterval(
      () => setAgentTokenAlertTick((t) => t + 1),
      60_000,
    );
    return () => window.clearInterval(id);
  }, [clientSlug]);

  // Parse SSO tokens from hash fragment
  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hash.length < 5) return;
    const params = new URLSearchParams(hash.substring(1));
    const accessToken = params.get("access_token");
    const refreshToken = params.get("refresh_token");
    const clientSlugParam = params.get("client_slug");
    const clientName = params.get("client_name");
    if (accessToken && refreshToken && clientSlugParam) {
      const client = {
        name: clientName || "",
        slug: clientSlugParam,
        logo_url: "",
      };
      setToken(accessToken);
      savePortalSession({
        token: accessToken,
        refreshToken,
        client,
        persistent: false,
      });
      setClientInfo(client);
      // Clean up hash
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, []);
  /* eslint-enable react-hooks/set-state-in-effect */

  /* eslint-disable react-hooks/set-state-in-effect */
  useEffect(() => {
    if (!clientSlug) return;
    const key = getPortalPolicyStorageKey(clientSlug);
    setHasAcceptedPlatformPolicy(localStorage.getItem(key) === "1");
  }, [clientSlug]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const loadDashboard = async () => {
    try {
      const [docsRes, subsRes, usersRes, quotasRes] = await Promise.all([
        fetchDocuments(api),
        fetchSubclients(api),
        fetchChatbotUsers(api).catch(() => ({ data: [] })),
        fetchPortalQuotas(api).catch(() => ({ data: {} })),
      ]);
      setDocuments(docsRes.data.documents || []);
      setMaxStorage(docsRes.data.max_storage_bytes || 524288000);
      setCurrentStorage(docsRes.data.current_storage_bytes || 0);
      setPortalSync(docsRes.data.sync || {});
      setSubclients(subsRes.data || []);
      setChatbotUsers(usersRes.data || []);
      setPortalQuotas(
        quotasRes.data && typeof quotasRes.data === "object"
          ? quotasRes.data
          : {},
      );
      try {
        const raw = getStoredPortalClient();
        const apiClient = docsRes.data.client || {};
        setClientInfo({
          name: apiClient.name || raw.name || clientInfo?.name || "",
          slug: apiClient.slug || raw.slug || raw.client_slug || "",
          logo_url:
            apiClient.logo_url || raw.logo_url || clientInfo?.logo_url || "",
          primary_color:
            apiClient.primary_color ||
            raw.primary_color ||
            clientInfo?.primary_color ||
            "#8d282a",
          secondary_color:
            apiClient.secondary_color ||
            raw.secondary_color ||
            clientInfo?.secondary_color ||
            "#010101",
        });
      } catch {
        setClientInfo((prev) => prev || { name: "", slug: "", logo_url: "" });
      }
    } catch {
      setToken("");
      clearPortalAuthStorage();
    }
  };

  /* eslint-disable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */
  useEffect(() => {
    if (token) loadDashboard();
  }, [token]);
  /* eslint-enable react-hooks/set-state-in-effect, react-hooks/exhaustive-deps */

  useEffect(() => {
    if (!documents.some((d) => d.status === "processing")) return;

    let delay = 3000;
    let timeoutId;

    const poll = async () => {
      try {
        const docsRes = await fetchDocuments(api);
        const docs = docsRes.data.documents || [];
        setDocuments(docs);
        setMaxStorage(docsRes.data.max_storage_bytes || 524288000);
        setCurrentStorage(docsRes.data.current_storage_bytes || 0);
        setPortalSync(docsRes.data.sync || {});

        if (docs.some((d) => d.status === "processing")) {
          // Backoff : 3s → 5s → 8s → 13s → max 30s
          delay = Math.min(delay * 1.6, 30000);
          timeoutId = setTimeout(poll, delay);
        }
      } catch (err) {
        console.error("Erreur polling documents:", err);
      }
    };

    timeoutId = setTimeout(poll, delay);
    return () => clearTimeout(timeoutId);
  }, [documents, api]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError("");
    const routeSlug = (() => {
      if (!slugFromRoute) return "";
      try {
        return decodeURIComponent(slugFromRoute).trim().toLowerCase();
      } catch {
        return slugFromRoute.trim().toLowerCase();
      }
    })();
    const slugTrim = routeSlug || slug.trim().toLowerCase();
    try {
      const res = await portalLogin(slugTrim, password);
      const {
        access_token,
        refresh_token,
        client_name,
        client_slug,
        client_logo_url,
        client_primary_color,
        client_secondary_color,
      } = res.data;
      const client = {
        name: client_name,
        slug: client_slug,
        logo_url: client_logo_url || "",
        primary_color: client_primary_color || "#8d282a",
        secondary_color: client_secondary_color || "#010101",
      };
      setToken(access_token);
      savePortalSession({
        token: access_token,
        refreshToken: refresh_token,
        client,
        persistent: true,
      });
      setClientInfo(client);
    } catch (err) {
      setLoginError(formatApiErrorDetail(err, "Erreur de connexion"));
    }
  };

  const handleSsoLogin = async () => {
    const routeSlug = (() => {
      if (!slugFromRoute) return "";
      try {
        return decodeURIComponent(slugFromRoute).trim().toLowerCase();
      } catch {
        return slugFromRoute.trim().toLowerCase();
      }
    })();
    const s = routeSlug || slug.trim().toLowerCase();
    try {
      const redirectTo = window.location.origin;
      const res = await axios.get("/api/sso/initiate", {
        params: { slug: s, auth_type: "portal", redirect_to: redirectTo },
      });
      window.location.href = res.data.authorization_url;
    } catch (err) {
      setLoginError(formatApiErrorDetail(err, "SSO non disponible"));
    }
  };

  const handleLogout = () => {
    setToken("");
    setClientInfo(null);
    clearPortalAuthStorage();
  };

  const handleRequestAgentSync = async () => {
    setSyncLoading(true);
    try {
      await requestDocumentSync(api);
      await loadDashboard();
    } catch (err) {
      console.error(err);
      alert(
        formatApiErrorDetail(
          err,
          "La synchronisation par agent local n'est pas disponible : l'administrateur doit d'abord activer l'agent local pour ce client, puis installer l'agent sur un poste.",
        ),
      );
    } finally {
      setSyncLoading(false);
    }
  };

  const handleCancelAgentSync = async () => {
    setSyncCancelLoading(true);
    try {
      await cancelDocumentSync(api);
      await loadDashboard();
    } catch (err) {
      console.error(err);
      alert(
        formatApiErrorDetail(
          err,
          "Impossible d'annuler la demande de synchronisation.",
        ),
      );
    } finally {
      setSyncCancelLoading(false);
    }
  };

  const MAX_BATCH_BYTES = 90 * 1024 * 1024;
  const CHUNK_SIZE = 80 * 1024 * 1024;

  const uploadLargeFile = async (file) => {
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    const formData = new FormData();
    formData.append("filename", file.webkitRelativePath || file.name);
    formData.append("total_chunks", totalChunks);
    formData.append("total_size", file.size);
    const initRes = await initChunkedUpload(api, formData, selectedSubclient);
    const uploadId = initRes.data.upload_id;
    for (let i = 0; i < totalChunks; i++) {
      const start = i * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      const chunkForm = new FormData();
      chunkForm.append("upload_id", uploadId);
      chunkForm.append("chunk_index", i);
      chunkForm.append("file", chunk, file.name);
      await uploadChunk(api, chunkForm);
    }
    const completeForm = new FormData();
    completeForm.append("upload_id", uploadId);
    const res = await completeChunkedUpload(api, completeForm);
    return res.data;
  };

  const processFiles = async (files) => {
    if (!files.length) return;
    const isFirstUpload = documents.length === 0;
    if (isFirstUpload && !hasAcceptedPlatformPolicy) {
      alert(
        "Veuillez d'abord cocher l'attestation de la politique de confidentialite de la plateforme.",
      );
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setIsUploading(true);
    const allErrors = [];
    try {
      let batch = [];
      let batchBytes = 0;

      const flushBatch = async () => {
        if (!batch.length) return;
        const formData = new FormData();
        for (const file of batch) {
          const name = file.webkitRelativePath || file.name;
          formData.append("files", file, name);
        }
        const res = await uploadFiles(api, formData, selectedSubclient);
        if (res.data?.results) {
          const errors = res.data.results.filter(
            (r) => r.status === "error" || r.status === "warning",
          );
          allErrors.push(...errors);
        }
        batch = [];
        batchBytes = 0;
      };

      for (const file of files) {
        const fileSize = file.size || 0;
        if (fileSize > MAX_BATCH_BYTES) {
          await flushBatch();
          try {
            const result = await uploadLargeFile(file);
            if (result?.results) {
              const errors = result.results.filter(
                (r) => r.status === "error" || r.status === "warning",
              );
              allErrors.push(...errors);
            }
          } catch (err) {
            allErrors.push({
              file: file.name,
              status: "error",
              message: formatUploadError(err),
            });
          }
          continue;
        }
        if (batch.length > 0 && batchBytes + fileSize > MAX_BATCH_BYTES) {
          await flushBatch();
        }
        batch.push(file);
        batchBytes += fileSize;
      }
      await flushBatch();

      if (allErrors.length > 0) {
        alert(
          "Résultat de l'import :\n\n" +
            allErrors.map((e) => `- ${e.file} : ${e.message}`).join("\n"),
        );
      }
      loadDashboard();
    } catch (err) {
      alert(formatUploadError(err));
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleUpload = async (e) => {
    await processFiles(Array.from(e.target.files));
  };

  const handleDroppedFiles = async (items) => {
    setIsUploading(true);
    try {
      const files = await collectDroppedItems(items);
      await processFiles(files);
    } catch (err) {
      alert(formatUploadError(err));
    } finally {
      setIsUploading(false);
    }
  };

  const handlePlatformPolicyAcceptedChange = (checked) => {
    if (!clientSlug) return;
    const key = getPortalPolicyStorageKey(clientSlug);
    if (checked) {
      localStorage.setItem(key, "1");
      setHasAcceptedPlatformPolicy(true);
    } else {
      localStorage.removeItem(key);
      setHasAcceptedPlatformPolicy(false);
    }
  };

  const handleDeleteDoc = async (doc) => {
    const docId = typeof doc === "number" ? doc : doc?.id;
    if (!docId) return;
    const isLocalAgent = (doc?.sync_origin || "") === "local_agent";
    const msg = isLocalAgent
      ? "Déplacer ce document dans la corbeille ?\n\nCe fichier sera aussi supprimé du dossier RAGUIA local synchronisé."
      : "Déplacer ce document dans la corbeille ?";
    if (!confirm(msg)) return;
    try {
      await deleteDocument(api, docId);
      setSelectedDocIds((prev) => prev.filter((id) => id !== docId));
      await loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur suppression"));
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedDocIds.length) return;
    const count = selectedDocIds.length;
    const hasLocalAgent = documents.some(
      (d) => selectedDocIds.includes(d.id) && d.sync_origin === "local_agent",
    );
    const msg = hasLocalAgent
      ? `Déplacer ${count} document${count > 1 ? "s" : ""} dans la corbeille ?\n\nLes fichiers synchronisés par l'agent local seront aussi supprimés du dossier RAGUIA.`
      : `Déplacer ${count} document${count > 1 ? "s" : ""} dans la corbeille ?`;
    if (!confirm(msg)) return;
    try {
      await bulkDeleteDocuments(api, selectedDocIds);
      setSelectedDocIds([]);
      await loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur suppression groupée"));
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      await createChatbotUser(api, {
        username: newUser.username,
        password: newUser.password,
        subclient_id: newUser.subclient_id,
      });
      setShowUserModal(false);
      setNewUser({
        username: "",
        password: "",
        subclient_id: null,
      });
      loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur création"));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm("Supprimer cet utilisateur ?")) return;
    try {
      await deleteChatbotUser(api, userId);
      loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur suppression"));
    }
  };

  const sumPercentsPortal = useMemo(
    () =>
      chatbotUsers.reduce((s, u) => s + (u.messages_monthly_percent ?? 0), 0),
    [chatbotUsers],
  );

  const handleEqualSplitPercents = async () => {
    if (!portalQuotas?.messages || chatbotUsers.length < 1) return;
    if (
      !confirm(
        "Réinitialiser les pourcentages et répartir équitablement 100 % entre tous les accès ? Les valeurs actuelles seront remplacées.",
      )
    )
      return;
    setEqualSplitLoading(true);
    try {
      await rebalanceMessagePercentsEqually(api);
      await loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur lors de la répartition"));
    } finally {
      setEqualSplitLoading(false);
    }
  };

  const handleEditUser = async (e) => {
    e.preventDefault();
    try {
      if (editUserPassword) {
        await updateChatbotUserPassword(api, editingUserId, editUserPassword);
      }
      const patch = {
        subclient_id: editUserSubclient === null ? 0 : editUserSubclient,
      };
      if (portalQuotas?.messages) {
        const maxEdit = Math.max(
          0,
          100 -
            chatbotUsers.reduce((s, u) => {
              if (u.id === editingUserId) return s;
              return s + (u.messages_monthly_percent ?? 0);
            }, 0),
        );
        const pctEdit = String(editUserPercent).trim();
        if (pctEdit === "") patch.messages_monthly_percent = null;
        else {
          const n = parseInt(pctEdit, 10);
          if (Number.isNaN(n) || n < 0 || n > maxEdit) {
            alert(
              `Indiquez un pourcentage entre 0 et ${maxEdit}, ou laissez vide pour retirer la limite sur cet accès.`,
            );
            return;
          }
          patch.messages_monthly_percent = n > 0 ? n : null;
        }
      }
      await updateChatbotUser(api, editingUserId, patch);
      setEditingUserId(null);
      setEditUserPassword("");
      setEditUserSubclient(null);
      setEditUserPercent("");
      loadDashboard();
      alert("Accès utilisateur mis à jour avec succès.");
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur modification"));
    }
  };

  const handleAssignDoc = async (e) => {
    e.preventDefault();
    try {
      await assignDocument(api, assignModal.docId, {
        subclient_ids: assignModal.selectedSid,
        general_access: assignModal.generalAccess,
      });
      setAssignModal(null);
      loadDashboard();
    } catch (err) {
      alert(formatApiErrorDetail(err, "Erreur assignation"));
    }
  };

  const toggleSubclientInModal = (sid) => {
    setAssignModal((prev) => {
      const current = prev.selectedSid.includes(sid)
        ? prev.selectedSid.filter((id) => id !== sid)
        : [...prev.selectedSid, sid];
      return { ...prev, selectedSid: current };
    });
  };

  const handleSelectAllSubclients = () => {
    setAssignModal((prev) => ({
      ...prev,
      selectedSid: subclients.map((s) => s.id),
      generalAccess: true,
    }));
  };

  const handleDeselectAllSubclients = () => {
    setAssignModal((prev) => ({
      ...prev,
      selectedSid: [],
      generalAccess: false,
    }));
  };

  const toggleSubclientInBulkModal = (sid) => {
    setBulkAccessModal((prev) => {
      if (!prev) return prev;
      const cur = prev.selectedSid.includes(sid)
        ? prev.selectedSid.filter((id) => id !== sid)
        : [...prev.selectedSid, sid];
      return { ...prev, selectedSid: cur };
    });
  };

  const handleBulkSelectAllSubs = () => {
    setBulkAccessModal((prev) =>
      prev
        ? {
            ...prev,
            selectedSid: subclients.map((s) => s.id),
          }
        : prev,
    );
  };

  const handleBulkDeselectAllSubs = () => {
    setBulkAccessModal((prev) =>
      prev ? { ...prev, selectedSid: [], generalAccess: false } : prev,
    );
  };

  const handleBulkAssignSave = async () => {
    if (!bulkAccessModal?.docIds?.length) return;
    try {
      for (const docId of bulkAccessModal.docIds) {
        await assignDocument(api, docId, {
          subclient_ids: bulkAccessModal.selectedSid,
          general_access: bulkAccessModal.generalAccess,
        });
      }
      setBulkAccessModal(null);
      setSelectedDocIds([]);
      loadDashboard();
    } catch (err) {
      alert(
        formatApiErrorDetail(err, "Erreur lors de l'application des accès"),
      );
    }
  };

  const openBulkAccessFromSelection = () => {
    if (!selectedDocIds.length) return;
    const initial = deriveBulkAccessInitialState(
      documents,
      selectedDocIds,
      subclients,
    );
    setBulkAccessModal({
      docIds: [...selectedDocIds],
      generalAccess: initial.generalAccess,
      selectedSid: initial.selectedSid,
    });
  };

  // Compatibilité anciennes sessions: si le logo n'est pas en localStorage,
  // on le récupère via la config publique du chatbot du même client.
  useEffect(() => {
    if (!clientSlug || !token || clientInfo?.logo_url) return;
    let cancelled = false;

    (async () => {
      try {
        const res = await fetch(
          `/api/c/${encodeURIComponent(clientSlug)}/config`,
        );
        if (!res.ok) return;
        const cfg = await res.json();
        if (cancelled || !cfg) return;

        setClientInfo((prev) => {
          const next = {
            name: prev?.name || cfg.name || "",
            slug: prev?.slug || cfg.slug || clientSlug,
            logo_url: cfg.logo_url || "",
            primary_color:
              cfg.primary_color || prev?.primary_color || "#8d282a",
            secondary_color:
              cfg.secondary_color || prev?.secondary_color || "#010101",
          };
          saveStoredPortalClient(next);
          return next;
        });
      } catch {
        // Ignore: le portail peut fonctionner sans logo.
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [clientSlug, token, clientInfo?.logo_url]);

  if (!token) {
    return (
      <PortalLoginView
        slug={slug}
        slugLocked={Boolean(slugFromRoute)}
        onSlugChange={setSlug}
        password={password}
        onPasswordChange={setPassword}
        loginError={loginError}
        onSubmit={handleLogin}
        hasSso={hasSso}
        onSsoLogin={handleSsoLogin}
      />
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 text-stone-800 flex flex-col relative overflow-hidden">
      <div className="pointer-events-none absolute inset-0 z-0 flex items-center justify-center">
        <img
          src="/raguia.webp"
          alt=""
          aria-hidden="true"
          className="w-[65vw] max-w-[900px] opacity-[0.08] object-contain select-none"
        />
      </div>

      <div className="relative z-10">
        <header className="bg-white border-b border-stone-200 sticky top-0 z-30 shadow-sm">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <div className="flex items-center gap-3">
                {clientInfo?.logo_url ? (
                  <div className="size-16 rounded-2xl overflow-hidden flex items-center justify-center">
                    <img
                      src={clientInfo.logo_url}
                      alt={`Logo ${clientInfo?.name || "client"}`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : null}
                <h1 className="text-lg font-semibold text-stone-800 leading-tight">
                  {clientInfo?.name || "Portail Client"}
                </h1>
              </div>
              <div className="flex items-center gap-2">
                <div className="hidden sm:flex size-9 rounded-md overflow-hidden items-center justify-center opacity-75 hover:opacity-100 transition-opacity">
                  <img
                    src="/raguia.webp"
                    alt="RAG IA"
                    className="w-full h-full object-contain"
                  />
                </div>
                {clientSlug && (
                  <a
                    href={`/c/${clientSlug}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-sm font-medium px-3 py-2 rounded-lg transition-colors border hover:brightness-95"
                    style={brandSoftStyle(clientInfo)}
                  >
                    <Bot size={16} />
                    <span className="hidden sm:inline">Chatbot</span>
                    <ExternalLink
                      size={12}
                      className="hidden sm:inline opacity-60"
                    />
                  </a>
                )}
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-2 text-sm font-medium text-stone-500 hover:text-red-600 hover:bg-red-50 px-3 py-2 rounded-lg transition-colors"
                >
                  <LogOut size={16} />{" "}
                  <span className="hidden sm:inline">Déconnexion</span>
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className="bg-white border-b border-stone-200 shadow-sm overflow-x-auto custom-scrollbar">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <nav className="flex gap-x-1 py-3" aria-label="Tabs">
              {[
                { id: "documents", label: "Documents", icon: FileText },
                { id: "subclients", label: "Sous-clients", icon: FolderOpen },
                { id: "users", label: "Accès Chatbot", icon: Users },
                { id: "settings", label: "Paramètres", icon: Settings },
              ].map((item) => (
                <button
                  key={item.id}
                  onClick={() => setTab(item.id)}
                  className={`
                  flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-xl whitespace-nowrap transition-all
                  ${
                    tab === item.id
                      ? "shadow-sm ring-1"
                      : "text-stone-500 hover:text-stone-700 hover:bg-stone-100"
                  }
                `}
                  style={
                    tab === item.id ? brandSoftStyle(clientInfo) : undefined
                  }
                >
                  <item.icon
                    size={16}
                    className={tab === item.id ? "" : "text-stone-400"}
                  />
                  {item.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {agentTokenBanner && (
          <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 w-full">
            <div
              role="alert"
              className={`flex flex-col sm:flex-row sm:items-center gap-3 p-4 rounded-xl border text-sm ${
                agentTokenBanner.type === "expired"
                  ? "bg-red-50 border-red-200 text-red-900"
                  : "bg-amber-50 border-amber-200 text-amber-950"
              }`}
            >
              <div className="flex items-start gap-3 flex-1 min-w-0">
                <AlertTriangle
                  className="shrink-0 mt-0.5"
                  size={20}
                  aria-hidden
                />
                <p className="leading-relaxed">{agentTokenBanner.text}</p>
              </div>
              <button
                type="button"
                onClick={() => setTab("settings")}
                className={`shrink-0 px-4 py-2 rounded-lg text-sm font-medium border transition-colors ${
                  agentTokenBanner.type === "expired"
                    ? "bg-white border-red-300 text-red-700 hover:bg-red-100"
                    : "bg-white border-amber-300 text-amber-900 hover:bg-amber-100"
                }`}
              >
                Paramètres → jeton agent
              </button>
            </div>
          </div>
        )}

        <main className="flex-1 max-w-7xl mx-auto px-4 sm:px-6 lg:p-8 w-full">
          {tab === "documents" && (
            <DocumentsTab
              clientInfo={clientInfo}
              documents={documents}
              subclients={subclients}
              selectedSubclient={selectedSubclient}
              setSelectedSubclient={setSelectedSubclient}
              fileInputRef={fileInputRef}
              handleUpload={handleUpload}
              handleDroppedFiles={handleDroppedFiles}
              handleDeleteDoc={handleDeleteDoc}
              setAssignModal={setAssignModal}
              formatSize={formatSize}
              isUploading={isUploading}
              maxStorage={maxStorage}
              currentStorage={currentStorage}
              selectedDocIds={selectedDocIds}
              setSelectedDocIds={setSelectedDocIds}
              onOpenBulkAccess={openBulkAccessFromSelection}
              onBulkDelete={handleBulkDelete}
              requirePolicyAcceptance={documents.length === 0}
              isPolicyAccepted={hasAcceptedPlatformPolicy}
              onPolicyAcceptedChange={handlePlatformPolicyAcceptedChange}
              portalSync={portalSync}
              onRequestAgentSync={handleRequestAgentSync}
              onCancelAgentSync={handleCancelAgentSync}
              syncLoading={syncLoading}
              syncCancelLoading={syncCancelLoading}
              localAgentEnabled={portalSync?.local_agent_enabled === true}
            />
          )}
          {tab === "subclients" && <SubClientsTab clientInfo={clientInfo} subclients={subclients} />}
          {tab === "users" && (
            <UsersTab
              clientInfo={clientInfo}
              quotas={portalQuotas}
              chatbotUsers={chatbotUsers}
              sumPercentsAssigned={sumPercentsPortal}
              onEqualSplitPercents={handleEqualSplitPercents}
              equalSplitLoading={equalSplitLoading}
              setShowUserModal={setShowUserModal}
              setEditingUserId={setEditingUserId}
              setEditUserSubclient={setEditUserSubclient}
              setEditUserPassword={setEditUserPassword}
              setEditUserPercent={setEditUserPercent}
              handleDeleteUser={handleDeleteUser}
            />
          )}
          {tab === "settings" && (
            <SettingsTab
              clientInfo={clientInfo}
              api={api}
              chatbotUsers={chatbotUsers}
              onRefresh={loadDashboard}
              clientSlug={clientSlug || ""}
              localAgentEnabled={portalSync?.local_agent_enabled === true}
              agentLastSyncAt={portalSync?.last_sync_at || null}
              agentLastError={portalSync?.last_error || null}
            />
          )}
        </main>
        <footer className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 w-full">
          <div className="border-t border-stone-200 pt-4 text-xs text-stone-500 flex items-center gap-3 flex-wrap">
            <a
              href="/confidentialite-plateforme"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-stone-700 underline"
            >
              Politique de confidentialite - Plateforme Raguia
            </a>
            <span className="text-stone-300">·</span>
            <a
              href="https://status.valentin-fiess.fr/status/raguia"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-stone-700 underline"
            >
              Statut du service
            </a>
          </div>
        </footer>

        {showUserModal && (
          <Modal
            title="Créer un accès chatbot"
            onClose={() => setShowUserModal(false)}
          >
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="space-y-1">
                <label htmlFor="new-user-username" className="text-sm font-medium text-stone-700">
                  Nom d'utilisateur
                </label>
                <input
                  id="new-user-username"
                  className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm"
                  placeholder="Ex: jean.dupont"
                  value={newUser.username}
                  onChange={(e) =>
                    setNewUser(prev => ({ ...prev, username: e.target.value }))
                  }
                  required
                />
              </div>
              <div className="space-y-1">
                <label htmlFor="new-user-password" className="text-sm font-medium text-stone-700">
                  Mot de passe
                </label>
                <div className="flex gap-2">
                  <input
                    id="new-user-password"
                    className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm"
                    type="text"
                    placeholder="••••••••"
                    value={newUser.password}
                    onChange={(e) =>
                      setNewUser({ ...newUser, password: e.target.value })
                    }
                    required
                  />
                  <button
                    type="button"
                    onClick={() =>
                      setNewUser({
                        ...newUser,
                        password: generatePassword(12),
                      })
                    }
                    className="px-3 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl transition-colors border border-stone-200"
                    title="Générer un mot de passe aléatoire"
                  >
                    <Wand2 size={18} />
                  </button>
                </div>
              </div>
              <div className="space-y-1">
                <label htmlFor="new-user-subclient" className="text-sm font-medium text-stone-700">
                  Périmètre d'accès
                </label>
                <select
                  id="new-user-subclient"
                  className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm"
                  value={newUser.subclient_id || ""}
                  onChange={(e) =>
                    setNewUser({
                      ...newUser,
                      subclient_id: e.target.value
                        ? parseInt(e.target.value)
                        : null,
                    })
                  }
                >
                  <option value="">Accès général (tous les documents)</option>
                  {subclients.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {portalQuotas?.messages ? (
                <p className="text-sm text-stone-600 rounded-xl border border-stone-200 bg-stone-50/80 px-4 py-3 leading-relaxed">
                  Après la création, la part des questions est{" "}
                  <strong>rééquilibrée automatiquement</strong> entre tous les
                  accès (100 % à parts égales). Vous pourrez affiner chaque
                  compte via <strong>Modifier l&apos;accès</strong>.
                </p>
              ) : null}
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors text-sm"
                  onClick={() => setShowUserModal(false)}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors text-sm shadow-sm"
                >
                  Créer l'accès
                </button>
              </div>
            </form>
          </Modal>
        )}

        {editingUserId && (
          <Modal
            title="Modifier l'accès"
            onClose={() => {
              setEditingUserId(null);
              setEditUserPercent("");
            }}
          >
            <form onSubmit={handleEditUser} className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-100 text-sm text-blue-800 mb-2">
                Utilisateur :{" "}
                <strong className="font-semibold">
                  {chatbotUsers.find((u) => u.id === editingUserId)?.username}
                </strong>
              </div>
              <div className="space-y-1">
                <label htmlFor="edit-user-subclient" className="text-sm font-medium text-stone-700">
                  Périmètre d'accès
                </label>
                <select
                  id="edit-user-subclient"
                  className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm"
                  value={editUserSubclient || ""}
                  onChange={(e) =>
                    setEditUserSubclient(
                      e.target.value ? parseInt(e.target.value) : null,
                    )
                  }
                >
                  <option value="">Accès général (tous les documents)</option>
                  {subclients.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>
              {portalQuotas?.messages
                ? (() => {
                    const sumOthers = chatbotUsers.reduce((s, u) => {
                      if (u.id === editingUserId) return s;
                      return s + (u.messages_monthly_percent ?? 0);
                    }, 0);
                    const maxAssign = Math.max(0, 100 - sumOthers);
                    const raw = String(editUserPercent ?? "").trim();
                    const parsed = raw === "" ? 0 : parseInt(raw, 10);
                    const cur =
                      Number.isNaN(parsed) || parsed < 0
                        ? 0
                        : Math.min(maxAssign, parsed);
                    const remaining = Math.max(0, 100 - sumOthers - cur);
                    const capTotal = portalQuotas.messages.max;
                    const msgsPreview =
                      cur > 0 && capTotal
                        ? messagesFromQuotaPercent(capTotal, cur)
                        : null;
                    return (
                      <div className="space-y-3 rounded-xl border border-stone-200 bg-stone-50/80 p-4">
                        <div className="flex flex-wrap items-baseline justify-between gap-2">
                          <label htmlFor="edit-user-percent" className="text-sm font-medium text-stone-800">
                            Part des questions pour cet accès
                            <span className="ml-1 text-xs font-normal text-stone-500">
                              (optionnel)
                            </span>
                          </label>
                          <span className="text-xs text-stone-600 tabular-nums">
                            Il reste{" "}
                            <strong className="text-blue-700">
                              {remaining}
                            </strong>{" "}
                            % à répartir
                          </span>
                        </div>
                        <input
                          id="edit-user-percent"
                          type="range"
                          min={0}
                          max={Math.max(0, maxAssign)}
                          step={1}
                          value={cur}
                          disabled={maxAssign === 0}
                          onChange={(e) => {
                            const v = Number(e.target.value);
                            setEditUserPercent(v === 0 ? "" : String(v));
                          }}
                          className="w-full h-2 accent-blue-600 disabled:opacity-40 cursor-pointer disabled:cursor-not-allowed"
                        />
                        <div className="flex items-center gap-3">
                          <span className="text-sm tabular-nums text-stone-700 w-12 shrink-0">
                            {cur}%
                          </span>
                          <input
                            type="number"
                            min={0}
                            max={maxAssign}
                            className="w-24 px-3 py-2 rounded-lg border border-stone-200 bg-white text-sm tabular-nums focus:outline-none focus:ring-2 focus:ring-blue-500/40"
                            value={raw === "" ? "" : cur}
                            disabled={maxAssign === 0}
                            onChange={(e) => {
                              const t = e.target.value;
                              if (t === "") {
                                setEditUserPercent("");
                                return;
                              }
                              const n = parseInt(t, 10);
                              if (Number.isNaN(n)) return;
                              const c = Math.max(0, Math.min(maxAssign, n));
                              setEditUserPercent(c === 0 ? "" : String(c));
                            }}
                          />
                          <span className="text-xs text-stone-500">
                            % (max {maxAssign})
                          </span>
                        </div>
                        {msgsPreview != null ? (
                          <p className="text-sm text-stone-700 tabular-nums">
                            ≈ <strong>{msgsPreview}</strong> message
                            {msgsPreview > 1 ? "s" : ""} / mois pour cet accès
                          </p>
                        ) : null}
                      </div>
                    );
                  })()
                : null}
              <div className="space-y-1 mt-4 border-t pt-4">
                <label htmlFor="edit-user-new-password" className="text-sm font-medium text-stone-700">
                  Nouveau mot de passe{" "}
                  <span className="text-xs text-stone-400 font-normal">
                    (optionnel)
                  </span>
                </label>
                <div className="flex gap-2">
                  <input
                    id="edit-user-new-password"
                    className="w-full px-4 py-2.5 rounded-xl bg-stone-50 border border-stone-200 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500 text-sm"
                    type="text"
                    placeholder="Minimum 4 caractères"
                    value={editUserPassword}
                    onChange={(e) => setEditUserPassword(e.target.value)}
                    minLength={4}
                  />
                  <button
                    type="button"
                    onClick={() => setEditUserPassword(generatePassword(12))}
                    className="px-3 py-2.5 bg-stone-100 hover:bg-stone-200 text-stone-600 rounded-xl transition-colors border border-stone-200"
                    title="Générer un mot de passe aléatoire"
                  >
                    <Wand2 size={18} />
                  </button>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  className="flex-1 px-4 py-2.5 rounded-xl border border-stone-200 text-stone-600 font-medium hover:bg-stone-50 transition-colors text-sm"
                  onClick={() => {
                    setEditingUserId(null);
                    setEditUserPercent("");
                  }}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-xl bg-blue-600 disabled:opacity-50 text-white font-medium hover:bg-blue-700 transition-colors text-sm shadow-sm"
                >
                  Mettre à jour
                </button>
              </div>
            </form>
          </Modal>
        )}

        {assignModal && (
          <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex justify-end animate-in fade-in duration-200">
            <div className="bg-white h-full w-full max-w-md shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
              <div className="px-6 py-5 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                  <Shield size={18} className="text-blue-500" /> Accès du
                  document
                </h3>
                <button
                  className="p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-600 rounded-lg transition-colors"
                  onClick={() => setAssignModal(null)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="p-6 space-y-4">
                <div className="flex justify-between items-center px-1">
                  <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    Périmètres de visibilité
                  </h4>
                  <div className="flex gap-4 text-xs font-bold">
                    <button
                      onClick={handleSelectAllSubclients}
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      Tout sélectionner
                    </button>
                    <span className="text-stone-200">•</span>
                    <button
                      onClick={handleDeselectAllSubclients}
                      className="text-stone-400 hover:text-stone-600 transition-colors"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                </div>

                <label
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group mb-2 ${assignModal.generalAccess ? "bg-brand-red/5 border-brand-red/30 ring-1 ring-brand-red/20" : "bg-white border-stone-100 hover:border-stone-200"}`}
                  onClick={() =>
                    setAssignModal((prev) => ({
                      ...prev,
                      generalAccess: !prev.generalAccess,
                    }))
                  }
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`size-10 rounded-full flex items-center justify-center shrink-0 font-bold text-xs ${assignModal.generalAccess ? "bg-brand-red text-white" : "bg-stone-100 text-stone-400"}`}
                    >
                      <Shield size={18} />
                    </div>
                    <div>
                      <p
                        className={`font-bold text-sm ${assignModal.generalAccess ? "text-brand-red" : "text-stone-700"}`}
                      >
                        Accès général
                      </p>
                      <p className="text-xs text-stone-400">
                        Visible par les utilisateurs sans sous-client
                      </p>
                    </div>
                  </div>
                  <div
                    className={`size-6 rounded-full border-2 flex items-center justify-center transition-all ${assignModal.generalAccess ? "bg-brand-red border-brand-red" : "border-stone-200 group-hover:border-stone-300"}`}
                  >
                    {assignModal.generalAccess && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                </label>

                <div className="space-y-2 max-h-[400px] overflow-y-auto pr-2 custom-scrollbar">
                  {subclients.map((s) => {
                    const isSelected = assignModal.selectedSid.includes(s.id);
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group ${isSelected ? "bg-blue-50 border-blue-200 ring-1 ring-blue-100" : "bg-white border-stone-100 hover:border-stone-200"}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`size-10 rounded-full flex items-center justify-center shrink-0 font-bold text-xs ${isSelected ? "bg-blue-600 text-white" : "bg-stone-100 text-stone-400"}`}
                          >
                            {s.name.substring(0, 2).toUpperCase()}
                          </div>
                          <p
                            className={`font-bold text-sm ${isSelected ? "text-blue-900" : "text-stone-700"}`}
                          >
                            {s.name}
                          </p>
                        </div>
                        <div
                          className={`size-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "bg-blue-600 border-blue-600" : "border-stone-200 group-hover:border-stone-300"}`}
                        >
                          {isSelected && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => toggleSubclientInModal(s.id)}
                        />
                      </label>
                    );
                  })}
                  {subclients.length === 0 && (
                    <div className="text-center p-6 text-stone-500 bg-stone-50 rounded-xl border border-dashed border-stone-200">
                      <p className="text-sm">Aucun sous-client créé.</p>
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-stone-400 text-center italic">
                  Le toggle « Accès général » contrôle la visibilité pour les
                  utilisateurs sans sous-client assigné.
                </p>
              </div>

              <div className="p-6 border-t border-stone-100 bg-stone-50/50 flex gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-3 rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-100 transition-colors text-sm"
                  onClick={() => setAssignModal(null)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors text-sm shadow-sm"
                  onClick={handleAssignDoc}
                >
                  Sauvegarder
                </button>
              </div>
            </div>
          </div>
        )}

        {bulkAccessModal && (
          <div className="fixed inset-0 bg-stone-900/40 backdrop-blur-sm z-50 flex justify-end animate-in fade-in duration-200">
            <div className="bg-white h-full w-full max-w-md shadow-2xl flex flex-col animate-in slide-in-from-right duration-300">
              <div className="px-6 py-5 border-b border-stone-100 flex justify-between items-center bg-stone-50/50">
                <h3 className="text-lg font-semibold text-stone-800 flex items-center gap-2">
                  <Shield size={18} className="text-blue-500" /> Accès groupé
                </h3>
                <button
                  type="button"
                  className="p-2 text-stone-400 hover:bg-stone-100 hover:text-stone-600 rounded-lg transition-colors"
                  onClick={() => setBulkAccessModal(null)}
                >
                  <X size={20} />
                </button>
              </div>

              <div className="px-6 py-3 bg-stone-100/80 border-b border-stone-100 text-sm text-stone-700 space-y-1">
                <p>
                  <strong>{bulkAccessModal.docIds.length}</strong> document
                  {bulkAccessModal.docIds.length > 1 ? "s" : ""} : le même accès
                  sera appliqué à tous.
                </p>
                <p className="text-xs text-stone-500">
                  Les cases reflètent l’intersection : coché seulement si{" "}
                  <em>tous</em> les documents sélectionnés ont déjà cet accès.
                  Ajustez puis validez pour harmoniser.
                </p>
              </div>

              <div className="p-6 space-y-4 flex-1 overflow-y-auto">
                <div className="flex justify-between items-center px-1">
                  <h4 className="text-xs font-semibold text-stone-400 uppercase tracking-wider">
                    Périmètres de visibilité
                  </h4>
                  <div className="flex gap-4 text-xs font-bold">
                    <button
                      type="button"
                      onClick={handleBulkSelectAllSubs}
                      className="text-blue-600 hover:text-blue-700 transition-colors"
                    >
                      Tout sélectionner
                    </button>
                    <span className="text-stone-200">•</span>
                    <button
                      type="button"
                      onClick={handleBulkDeselectAllSubs}
                      className="text-stone-400 hover:text-stone-600 transition-colors"
                    >
                      Tout désélectionner
                    </button>
                  </div>
                </div>

                <label
                  className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group mb-2 ${bulkAccessModal.generalAccess ? "bg-brand-red/5 border-brand-red/30 ring-1 ring-brand-red/20" : "bg-white border-stone-100 hover:border-stone-200"}`}
                  onClick={() =>
                    setBulkAccessModal((prev) =>
                      prev
                        ? { ...prev, generalAccess: !prev.generalAccess }
                        : prev,
                    )
                  }
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`size-10 rounded-full flex items-center justify-center shrink-0 font-bold text-xs ${bulkAccessModal.generalAccess ? "bg-brand-red text-white" : "bg-stone-100 text-stone-400"}`}
                    >
                      <Shield size={18} />
                    </div>
                    <div>
                      <p
                        className={`font-bold text-sm ${bulkAccessModal.generalAccess ? "text-brand-red" : "text-stone-700"}`}
                      >
                        Accès général
                      </p>
                      <p className="text-xs text-stone-400">
                        Visible par les utilisateurs sans sous-client
                      </p>
                    </div>
                  </div>
                  <div
                    className={`size-6 rounded-full border-2 flex items-center justify-center transition-all ${bulkAccessModal.generalAccess ? "bg-brand-red border-brand-red" : "border-stone-200 group-hover:border-stone-300"}`}
                  >
                    {bulkAccessModal.generalAccess && (
                      <Check size={14} className="text-white" />
                    )}
                  </div>
                </label>

                <div className="space-y-2 max-h-[360px] overflow-y-auto pr-2 custom-scrollbar">
                  {subclients.map((s) => {
                    const isSelected = bulkAccessModal.selectedSid.includes(
                      s.id,
                    );
                    return (
                      <label
                        key={s.id}
                        className={`flex items-center justify-between p-4 rounded-xl border transition-all cursor-pointer group ${isSelected ? "bg-blue-50 border-blue-200 ring-1 ring-blue-100" : "bg-white border-stone-100 hover:border-stone-200"}`}
                      >
                        <div className="flex items-center gap-3">
                          <div
                            className={`size-10 rounded-full flex items-center justify-center shrink-0 font-bold text-xs ${isSelected ? "bg-blue-600 text-white" : "bg-stone-100 text-stone-400"}`}
                          >
                            {s.name.substring(0, 2).toUpperCase()}
                          </div>
                          <p
                            className={`font-bold text-sm ${isSelected ? "text-blue-900" : "text-stone-700"}`}
                          >
                            {s.name}
                          </p>
                        </div>
                        <div
                          className={`size-6 rounded-full border-2 flex items-center justify-center transition-all ${isSelected ? "bg-blue-600 border-blue-600" : "border-stone-200 group-hover:border-stone-300"}`}
                        >
                          {isSelected && (
                            <Check size={14} className="text-white" />
                          )}
                        </div>
                        <input
                          type="checkbox"
                          className="hidden"
                          checked={isSelected}
                          onChange={() => toggleSubclientInBulkModal(s.id)}
                        />
                      </label>
                    );
                  })}
                  {subclients.length === 0 && (
                    <div className="text-center p-6 text-stone-500 bg-stone-50 rounded-xl border border-dashed border-stone-200 text-sm">
                      Aucun sous-client créé.
                    </div>
                  )}
                </div>

                <p className="text-[11px] text-stone-400 text-center italic">
                  Même logique que « Gérer l&apos;accès » sur un seul document.
                </p>
              </div>

              <div className="p-6 border-t border-stone-100 bg-stone-50/50 flex gap-3">
                <button
                  type="button"
                  className="flex-1 px-4 py-3 rounded-xl border border-stone-200 text-stone-700 font-medium hover:bg-stone-100 transition-colors text-sm"
                  onClick={() => setBulkAccessModal(null)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="flex-1 px-4 py-3 rounded-xl bg-blue-600 text-white font-medium hover:bg-blue-700 transition-colors text-sm shadow-sm"
                  onClick={handleBulkAssignSave}
                >
                  Appliquer à tous
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
